import { useState, useMemo } from 'react';

import { menuHelpers } from '../Menu';
import type { MenuItemProps } from '../Menu';
import type { MenuProps } from '../Menu/Menu.types';
import { useI18n, useTestIds } from '../../hooks';
import type { BaseProps, NoChildrenProp, TestIdProp } from '../../types';
import Flex from '../Flex';
import { ThemeOverride, type ConfigurationProps } from '../Configuration';
import MenuButton from '../MenuButton';
import { withTestIds } from '../../utils';
import Icon from '../Icon';

import { getThemeSwitcherTestIds } from './ThemeSwitcher.test-ids';

const ThemeModeIconMap = {
  light: 'sun',
  dark: 'moon',
  system: 'circle-mixed-left'
} as const;

export interface ThemeSwitcherProps extends BaseProps, NoChildrenProp, TestIdProp {
  themeMode: NonNullable<ConfigurationProps['themeMode']>;
  onThemeModeChange: (mode: ThemeSwitcherProps['themeMode']) => void;
}

const ThemeSwitcher = withTestIds(function ThemeSwitcher({
  testId,
  themeMode,
  onThemeModeChange
}: ThemeSwitcherProps) {
  const t = useI18n();
  const testIds = useTestIds(testId, getThemeSwitcherTestIds);

  const [items, setItems] = useState<MenuProps['items']>(() => [
    {
      id: 'light',
      primary: 'Light',
      visual: <Icon name={ThemeModeIconMap.light} set='budicon' />,
      selected: themeMode === 'light'
    },
    {
      id: 'dark',
      primary: 'Dark',
      visual: <Icon name={ThemeModeIconMap.dark} set='budicon' />,
      selected: themeMode === 'dark'
    },
    {
      id: 'system',
      primary: 'System',
      visual: <Icon name={ThemeModeIconMap.system} set='budicon' />,
      selected: !themeMode || themeMode === 'system'
    }
  ]);

  const handleMenuItemClick = (id: MenuItemProps['id']) => {
    setItems(cur => menuHelpers.selectItem(cur, id, 'single-select'));
    onThemeModeChange?.(id as ThemeSwitcherProps['themeMode']);
  };

  const selectedThemeIcon = useMemo(() => {
    const selected = menuHelpers.getSelected(items)[0];
    return selected
      ? ThemeModeIconMap[selected.id as keyof typeof ThemeModeIconMap]
      : ThemeModeIconMap.system;
  }, [items]);

  return (
    <Flex
      container={{
        alignItems: 'center'
      }}
    >
      <ThemeOverride theme={{ base: { 'icon-set': 'budicon' } }}>
        <MenuButton
          text={t('theme_mode')}
          data-testid={testIds.root}
          icon={selectedThemeIcon}
          iconOnly
          variant='simple'
          menu={{
            mode: 'single-select',
            items,
            onItemClick: handleMenuItemClick
          }}
        />
      </ThemeOverride>
    </Flex>
  );
}, getThemeSwitcherTestIds);

export default ThemeSwitcher;
